import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { RefreshCw, UserPlus, ChevronDown, ChevronUp, UserCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface Profile {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  user_follows: { count: number }[];
}

export const SuggestedFollows = ({ showAfterFollow = false }: { showAfterFollow?: boolean }) => {
  const [offset, setOffset] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [followingId, setFollowingId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const limit = 3;

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300; // Adjust this value to control scroll distance
      const newScrollPosition = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const { data: suggestedUsers, isLoading } = useQuery({
    queryKey: ["suggested-follows", offset],
    queryFn: async () => {
      const { data: users, error } = await supabase
        .from('profiles')
        .select(`
          id,
          username,
          full_name,
          avatar_url,
          bio,
          user_follows:user_follows!following_id(count)
        `)
        .neq('id', user?.id);

      if (error) throw error;
      
      const { data: follows, error: followsError } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user?.id);

      if (followsError) throw followsError;

      const followingIds = follows?.map(f => f.following_id) || [];
      
      const transformedUsers = (users as Profile[] || [])
        .filter(u => !followingIds.includes(u.id))
        .map(user => ({
          ...user,
          follower_count: user.user_follows[0]?.count || 0
        }))
        .sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0))
        .slice(offset, offset + limit);

      return transformedUsers;
    },
    enabled: !!user,
  });

  const followMutation = useMutation({
    mutationFn: async (userId: string) => {
      setFollowingId(userId);
      await new Promise(resolve => setTimeout(resolve, 800)); // Delay for animation

      const { data: existingFollow } = await supabase
        .from('user_follows')
        .select('*')
        .eq('follower_id', user?.id)
        .eq('following_id', userId)
        .single();

      if (existingFollow) {
        throw new Error('You are already following this user');
      }

      const { error } = await supabase
        .from('user_follows')
        .insert([
          {
            follower_id: user?.id,
            following_id: userId,
          },
        ]);

      if (error) throw error;
      
      // Add a small delay before removing the card
      await new Promise(resolve => setTimeout(resolve, 400));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suggested-follows"] });
      toast({
        title: "Success",
        description: "You are now following this user",
      });
      setFollowingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setFollowingId(null);
    },
  });

  const handleFollow = (userId: string) => {
    followMutation.mutate(userId);
  };

  const handleRefresh = () => {
    setOffset(prev => prev + limit);
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-4 bg-black/20 rounded-lg">
        <h2 className="font-semibold text-lg">Suggested Follows</h2>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-700 rounded w-24" />
                <div className="h-3 bg-gray-700 rounded w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!suggestedUsers?.length) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 right-0 mb-4 mr-4 z-50 transition-all duration-300 transform 
      ${showAfterFollow ? (isExpanded ? 'translate-y-0' : 'translate-y-[90%]') : ''}`}
    >
      <div className="bg-black/90 rounded-lg shadow-lg border border-flux-cyan/20 w-[340px]">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h2 className="font-semibold text-lg text-white">Suggested Follows</h2>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          )}
        </div>
        
        <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
          <div className="p-4 space-y-4">
            <div className="relative">
              <button 
                onClick={() => handleScroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 bg-black/80 p-1.5 rounded-full border border-flux-cyan/20 hover:bg-black/90 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-flux-cyan" />
              </button>
              <button 
                onClick={() => handleScroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 bg-black/80 p-1.5 rounded-full border border-flux-cyan/20 hover:bg-black/90 transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-flux-cyan" />
              </button>

              <div 
                ref={scrollContainerRef}
                className="flex overflow-x-auto space-x-4 pb-2 custom-scrollbar scroll-smooth"
              >
                {suggestedUsers.map((suggestedUser) => (
                  <div key={suggestedUser.id} className="flex-none w-64">
                    <div className={`bg-black/50 p-4 rounded-lg space-y-4 border border-flux-cyan/10 hover:border-flux-cyan/30 transition-all duration-300
                      ${followingId === suggestedUser.id ? 'follow-highlight' : ''}`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-12 h-12 ring-2 ring-flux-cyan/20">
                          <AvatarImage src={suggestedUser.avatar_url || ""} />
                          <AvatarFallback>{suggestedUser.username?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-white">{suggestedUser.full_name}</p>
                          <p className="text-sm text-gray-400 truncate">@{suggestedUser.username}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-300 line-clamp-2 min-h-[40px]">
                        {suggestedUser.bio || "No bio available"}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full bg-flux-cyan/10 hover:bg-flux-cyan/20 border-flux-cyan/30"
                        onClick={() => handleFollow(suggestedUser.id)}
                        disabled={followMutation.isPending}
                      >
                        {followingId === suggestedUser.id ? (
                          <div className="flex items-center justify-center gap-2 text-flux-cyan">
                            <UserCheck className="w-4 h-4 animate-scale-in" />
                            <span className="animate-fade-in">Followed</span>
                          </div>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Follow
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-gray-400 hover:text-white hover:bg-flux-cyan/10"
              onClick={handleRefresh}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Show More
            </Button>
          </div>
        </div>
      </div>

      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            height: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(0, 255, 255, 0.2);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 255, 255, 0.3);
          }
          .follow-highlight {
            background: linear-gradient(45deg, rgba(0, 255, 255, 0.1), rgba(0, 128, 128, 0.2));
            border-color: rgba(0, 255, 255, 0.4) !important;
            transform: scale(1.02);
          }
          @keyframes scale-in {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .animate-scale-in {
            animation: scale-in 0.3s ease-out forwards;
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};
